import { useQuery } from "@tanstack/react-query";
import { useWorkspace } from "@/hooks/use-workspace";

/**
 * "Powered by Clarik" attribution rendered at the bottom of every
 * client-facing surface (public booking page, client portal, public
 * invoice viewer, public knowledge mirror).
 *
 * The toggle is tier-gated server-side: Starter / Lite workspaces always
 * receive `show=true` (the PATCH that would clear it returns 403). Paid
 * tiers default to FALSE the first time they cross into a paid plan and
 * become admin-editable. Callers must pass `show` explicitly so a missing
 * prop fails loudly instead of silently hiding it everywhere.
 *
 * When `label` is omitted, the component falls back to whatever the
 * `/api/branding/public` endpoint resolves — workspace `poweredByText`
 * if the tier allows custom branding, otherwise the platform default
 * configured by the super-admin in Default Identity, otherwise the
 * bundled string "Powered by Clarik". This means individual callers
 * don't have to thread the value through their own data fetches.
 */
interface PoweredByAttributionProps {
  show: boolean;
  label?: string | null;
  className?: string;
}

export function PoweredByAttribution({ show, label, className }: PoweredByAttributionProps) {
  const { workspaceSlug } = useWorkspace();
  // Workspace-scoped key so switching tenants doesn't serve another
  // workspace's cached poweredByText.
  const { data: branding } = useQuery<{ poweredByText?: string | null }>({
    queryKey: ["/api/branding/public", workspaceSlug],
    enabled: show && !label,
    staleTime: 5 * 60_000,
  });
  if (!show) return null;
  const text =
    (label && label.trim()) ||
    (branding?.poweredByText && branding.poweredByText.trim()) ||
    "Powered by Clarik.io";
  return (
    <p
      className={
        className ??
        "text-xs text-muted-foreground/70 tracking-wide text-center mt-3"
      }
      data-testid="text-powered-by-attribution"
    >
      <a
        href="https://clarik.io"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
      >
        {text}
      </a>
    </p>
  );
}
